import React, { useMemo, useState } from 'react';
import { PlusCircleIcon, DocumentTextIcon, LinkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import { deleteNote } from 'views/notepad/moduleServices/notepad_services';
import { useEmbeddedNotepadData } from './useEmbeddedNotepadData';
import { useEmbeddedNotepadClusterLink } from './useEmbeddedNotepadClusterLink';
import { useEmbeddedNotepadCardViewMode } from './useEmbeddedNotepadCardViewMode';
import EmbeddedNotepadAccessDenied from './EmbeddedNotepadAccessDenied';
import EmbeddedNotepadClusterPicker from './EmbeddedNotepadClusterPicker';
import EmbeddedNotepadClusterSettingsMenu from './EmbeddedNotepadClusterSettingsMenu';
import EmbeddedNotepadNoteCard from './EmbeddedNotepadNoteCard';
import EmbeddedNotepadNoteDrawer from './EmbeddedNotepadNoteDrawer';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';

const LAYOUT_STYLES = {
  card: {
    clusterHeader:
      'mb-4 flex items-center justify-between gap-3 rounded-lg bg-gray-100 px-3 py-2',
    clusterTitle: 'truncate text-sm font-medium text-gray-800',
    notesWrapper: '',
    notesGrid: 'grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3',
    emptyLink: 'rounded-xl border border-dashed border-gray-300 bg-gray-50/80 p-5',
    emptyLinkSaving:
      'flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/80 py-12',
    missingCluster: 'rounded-xl border border-amber-200 bg-amber-50 p-5',
    emptyNotes: 'flex flex-col items-center justify-center py-12 text-gray-400',
    loadingNotes: 'grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3',
  },
  'module-tab': {
    clusterHeader:
      'bg-white rounded-2xl shadow flex items-center justify-between min-h-12 m-2 p-4',
    clusterTitle: 'truncate text-lg font-semibold text-gray-800',
    notesWrapper: 'p-2',
    notesGrid: 'grid grid-cols-1 md:grid-cols-3 gap-2',
    emptyLink: 'm-2 rounded-xl border border-dashed border-gray-300 bg-gray-50/80 p-5',
    emptyLinkSaving:
      'm-2 flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/80 py-12',
    missingCluster: 'm-2 rounded-xl border border-amber-200 bg-amber-50 p-5',
    emptyNotes: 'flex flex-col items-center justify-center min-h-[400px] text-gray-400',
    loadingNotes: 'grid grid-cols-1 md:grid-cols-3 gap-2',
  },
};

/**
 * Cluster-linked notes view: one Notepad cluster drives a filtered notes list + FAB create.
 */
const EmbeddedNotepadClusterNotesView = ({
  headerDescription,
  fetchClusterLink,
  patchClusterLink,
  onLoadError,
  onLinkError,
  noteCardViewMode = 'preview',
  emptyNotesMessage = 'No notes in this cluster yet.',
  layoutVariant = 'card',
  fabClassName = 'bg-yellow-500 hover:bg-yellow-600',
}) => {
  const layout = LAYOUT_STYLES[layoutVariant] || LAYOUT_STYLES.card;

  const { viewMode: noteCardViewModeState, showPreview, toggleViewMode } =
    useEmbeddedNotepadCardViewMode(noteCardViewMode);

  const {
    hasNotepadAccess,
    notes,
    clusters,
    isNotesLoading,
    isClustersLoading,
    isNotesLoadingNetwork,
    refreshNotes,
    updateNotesOptimistically,
  } = useEmbeddedNotepadData();

  const {
    clusterId,
    loading: linkLoading,
    saving: linkSaving,
    loadedOnce: linkLoadedOnce,
    saveClusterLink,
    clearClusterLink,
  } = useEmbeddedNotepadClusterLink({
    enabled: hasNotepadAccess,
    fetchClusterLink,
    patchClusterLink,
    onLoadError,
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [submittingClusterId, setSubmittingClusterId] = useState(null);
  const [drawerNoteId, setDrawerNoteId] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);

  const linkedCluster = useMemo(() => {
    if (!clusterId) return null;
    return clusters.find((c) => String(c.id) === String(clusterId)) || null;
  }, [clusters, clusterId]);

  const clusterNotes = useMemo(() => {
    if (!clusterId) return [];
    return notes
      .filter((n) => String(n.cluster?.id) === String(clusterId))
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        return dateB - dateA;
      });
  }, [notes, clusterId]);

  const drawerNote = useMemo(() => {
    if (!drawerNoteId) return null;
    return clusterNotes.find((n) => String(n.id) === String(drawerNoteId))
      || notes.find((n) => String(n.id) === String(drawerNoteId))
      || null;
  }, [drawerNoteId, clusterNotes, notes]);

  const lockedClusterId = clusterId ? Number(clusterId) : null;
  const isResolvingCluster = !!clusterId && !linkedCluster && (isClustersLoading || isNotesLoadingNetwork);
  const notesLoading = isNotesLoading && clusterNotes.length === 0 && !!clusterId;

  const handleSelectCluster = (cluster) => {
    setSubmittingClusterId(cluster.id);
    setPickerOpen(false);

    saveClusterLink(cluster.id)
      .then(() => toast.success('Cluster linked'))
      .catch((err) => onLinkError?.(err))
      .finally(() => setSubmittingClusterId(null));
  };

  const handleDeleteNote = async (noteId) => {
    await deleteNote(noteId);
    await refreshNotes();
    if (String(drawerNoteId) === String(noteId)) {
      setDrawerNoteId(null);
    }
  };

  const handleUnlinkCluster = async () => {
    try {
      await clearClusterLink();
      toast.success('Cluster unlinked');
    } catch (err) {
      onLinkError?.(err);
    }
  };

  if (!hasNotepadAccess) {
    return <EmbeddedNotepadAccessDenied />;
  }

  if (linkLoading || !linkLoadedOnce) {
    return (
      <div className="flex justify-center py-10">
        <SpinnerLoad />
      </div>
    );
  }

  return (
    <>
      {headerDescription && (
        <p className="mb-4 text-sm text-gray-600">{headerDescription}</p>
      )}

      {!clusterId ? (
        linkSaving ? (
          <div className={layout.emptyLinkSaving}>
            <SpinnerLoad />
            <p className="mt-3 text-sm text-gray-500">Saving cluster link...</p>
          </div>
        ) : (
          <div className={layout.emptyLink}>
            <p className="mb-3 text-sm text-gray-600">
              Link a Notepad cluster to list and add notes here.
            </p>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 hover:bg-gray-50"
            >
              <LinkIcon className="h-4 w-4 text-gray-500" />
              Link cluster
            </button>
          </div>
        )
      ) : isResolvingCluster || (linkSaving && !linkedCluster) ? (
        <div className="flex flex-col items-center justify-center py-10">
          <SpinnerLoad />
          {linkSaving && (
            <p className="mt-3 text-sm text-gray-500">Saving cluster link...</p>
          )}
        </div>
      ) : !linkedCluster ? (
        <div className={layout.missingCluster}>
          <div className="mb-3 flex items-start gap-2 text-sm text-amber-800">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
            <span>Linked cluster not found. It may have been deleted.</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
            >
              <LinkIcon className="h-4 w-4" />
              Relink cluster
            </button>
            <button
              type="button"
              onClick={handleUnlinkCluster}
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
            >
              Clear link
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className={`${layout.clusterHeader} ${linkSaving ? 'opacity-80' : ''}`}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-full"
                style={{ backgroundColor: linkedCluster.color || '#9CA3AF' }}
              />
              <h2 className={layout.clusterTitle}>{linkedCluster.name}</h2>
              {linkSaving ? (
                <span className="text-xs text-gray-500">Saving...</span>
              ) : !notesLoading && (
                <span className="text-xs text-gray-500">({clusterNotes.length})</span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {linkSaving && (
                <div className="flex items-center justify-center">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                </div>
              )}
              <EmbeddedNotepadClusterSettingsMenu
                showPreview={showPreview}
                onToggleViewMode={toggleViewMode}
                onChangeCluster={() => setPickerOpen(true)}
                onUnlinkCluster={handleUnlinkCluster}
                disabled={linkSaving}
              />
            </div>
          </div>

          <div className={layout.notesWrapper}>
            {notesLoading ? (
              <div className={layout.loadingNotes}>
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="animate-pulse rounded-xl bg-white p-3 shadow">
                    <div className="mb-2 h-5 rounded bg-gray-200" />
                    <div className="h-4 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : clusterNotes.length === 0 ? (
              <div className={layout.emptyNotes}>
                <DocumentTextIcon className="mb-3 h-12 w-12 text-gray-300" />
                <p className="text-sm">{emptyNotesMessage}</p>
              </div>
            ) : (
              <div className={layout.notesGrid}>
                {clusterNotes.map((note) => (
                  <EmbeddedNotepadNoteCard
                    key={note.id}
                    note={note}
                    viewMode={noteCardViewModeState}
                    onClick={() => setDrawerNoteId(note.id)}
                  />
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setCreateDrawerOpen(true)}
            disabled={linkSaving}
            className={`fixed bottom-20 right-6 rounded-full p-3 text-white shadow-lg disabled:cursor-not-allowed disabled:opacity-60 ${fabClassName}`}
            aria-label="Add note"
          >
            <PlusCircleIcon className="h-8 w-8" />
          </button>
        </>
      )}

      <EmbeddedNotepadClusterPicker
        isOpen={pickerOpen}
        onClose={() => !linkSaving && setPickerOpen(false)}
        clusters={clusters}
        isLoading={isClustersLoading}
        isSubmitting={linkSaving}
        submittingClusterId={submittingClusterId}
        title="Link Notepad cluster"
        onSelect={handleSelectCluster}
      />

      <EmbeddedNotepadNoteDrawer
        isOpen={!!drawerNoteId && !!drawerNote}
        onClose={() => setDrawerNoteId(null)}
        editNote={drawerNote}
        clusters={clusters}
        lockedClusterId={lockedClusterId}
        onOptimisticUpdate={updateNotesOptimistically}
        onDelete={handleDeleteNote}
      />

      <EmbeddedNotepadNoteDrawer
        isOpen={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        editNote={null}
        clusters={clusters}
        lockedClusterId={lockedClusterId}
        onOptimisticUpdate={updateNotesOptimistically}
        onCreateSuccess={() => {
          setCreateDrawerOpen(false);
          refreshNotes();
        }}
      />
    </>
  );
};

export default EmbeddedNotepadClusterNotesView;
